import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Vault } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await register(form);
      toast.success("Vault created");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md bg-white rounded-lg border border-slate-200 p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-10 w-10 rounded-md bg-slate-900 flex items-center justify-center">
            <Vault className="h-5 w-5 text-white" strokeWidth={1.8} />
          </div>
          <div className="font-heading font-semibold text-slate-900">Family Vault</div>
        </div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">
          Create your vault
        </h1>
        <p className="text-slate-600 mt-2 text-sm">
          Takes 90 seconds. You can add family, nominees and modules later.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
          <div>
            <Label htmlFor="full_name">Full Name</Label>
            <Input id="full_name" required
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              data-testid="register-fullname-input" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="register-email-input" />
          </div>
          <div>
            <Label htmlFor="phone">Mobile (optional)</Label>
            <Input id="phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="register-phone-input" />
          </div>
          <div>
            <Label htmlFor="password">Password (min 6 chars)</Label>
            <Input id="password" type="password" required minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="register-password-input" />
          </div>
          {error && <div className="text-sm text-red-600" data-testid="register-error">{error}</div>}
          <Button type="submit" className="w-full" disabled={busy}
            data-testid="register-submit-button">
            {busy ? "Creating..." : "Create vault"}
          </Button>
        </form>
        <p className="mt-6 text-sm text-slate-600">
          Already have a vault?{" "}
          <Link to="/login" className="font-medium text-slate-900 underline underline-offset-4"
            data-testid="link-to-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
